import React from "react";

import Sheldon from "@common/assets/images/ai/sheldon.png";
import Privacy from "@common/assets/images/privacy.svg";
import ThumbsUp from "@common/assets/images/thumbs_up.svg";
import Sparkle from "@common/assets/images/ticket-icons/sparkle-icon.png";
import ArrowLeft from "@common/assets/images/tiny-chat-triangle.svg";
import { BaseDialog, useBaseDialog } from "@common/components/Dialog/BaseDialog";
import displayToast from "@common/components/Toast/displayToast";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { Tag } from "@common/models";
import { Issue } from "@common/models/issueTracking";
import { IssueAI } from "@common/models/issueTracking/IssueAI.model";
import { TicketPriority } from "@common/models/TicketPriority.model";
import { vars } from "@common/styles";
import { docsLinks } from "@common/util/docs";
import { helpCenterArticles } from "@common/util/helpCenterArticles";
import { showIntercomArticle } from "@common/util/intercom";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import {
  AiAssistant,
  AiAssistantButton,
  aiAssistants,
} from "@main/pages/shared/components/SheldonSummary/AiAssistantButton/AiAssistantButton";
import { AIFeedbackModal } from "@main/pages/shared/components/SheldonSummary/AIFeedbackModal/AIFeedbackModal";
import * as Styled from "@main/pages/shared/components/SheldonSummary/SheldonSummary.styles";
import { RoutePaths } from "@main/router/config/routePaths";
import { Flex } from "@public/layouts/Layouts.styles";
import Linkify from "linkify-react";
import { generatePath, Link, useNavigate, useParams } from "react-router-dom";

interface Props {
  llmResponse?: IssueAI;
  userFeedbackId?: string;
  onGenerateResponse: () => Promise<IssueAI | undefined>;
  onAddTag: (tag: Tag) => void;
  onChangePriority: (priority: TicketPriority) => void;
  onMergeDuplicate?: () => void;
  onRemoveDuplicate?: (issueId: string) => void;
  duplicateSuggestion?: Issue | null;
  showDuplicate: boolean;
  onAiAssistantClick: (assistant: AiAssistant) => void;
  isPreparingAiPrompt: boolean;
}

export default function SheldonSummary({
  llmResponse,
  userFeedbackId,
  onGenerateResponse,
  onAddTag,
  onChangePriority,
  duplicateSuggestion,
  onMergeDuplicate,
  onRemoveDuplicate,
  showDuplicate,
  onAiAssistantClick,
  isPreparingAiPrompt,
}: Props) {
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  const navigate = useNavigate();
  const params = useParams<{
    workspaceSlug: string;
    appKey: string;
    userFeedbackKey: string;
  }>();
  const cancelModal = useBaseDialog({});

  const searchParams = {
    searchText: "",
    networkRequestTypes: {
      all: true,
      fetch: false,
      document: false,
      stylesheet: false,
      script: false,
      font: false,
      image: false,
      media: false,
      manifest: false,
      ws: false,
      wasm: false,
      other: false,
    },
    consoleLogs: true,
    systemEvents: true,
    userActions: true,
    screenViews: true,
    notificationEvents: true,
    customLogs: true,
    errorsOnly: false,
    ai: true,
  };

  const changePriority = () => {
    const priorityMap: Record<string, TicketPriority> = {
      high: TicketPriority.HIGH,
      medium: TicketPriority.MEDIUM,
      low: TicketPriority.LOW,
    };

    const mappedPriority = llmResponse?.priority ? priorityMap[llmResponse.priority.toLowerCase()] : undefined;

    if (mappedPriority) {
      onChangePriority(mappedPriority);
    }
  };

  const submitTally = () => {
    // TODO: add silent submit to tally
    // fetch("https://api.tally.so/forms/31ao4O/respond", {
    //   "headers": {
    //     method: "POST",
    //     body: JSON.stringify({
    //       sessionUuid: "7430d9ce-7d24-4247-83a7-28245095f071",
    //       respondentUuid: "908be6bb-8257-4b40-8d9f-60fc3e1b9398",
    //       responses:{
    //         "2c693169-e005-4563-b5db-c06eb5c68fa7": {
    //           "548d7286-9f0c-4232-841d-784808077b7b": "9dce720d-edbe-4b31-be08-c8384c52da3f"
    //       },
    //       "2839c95a-df64-423d-a133-b901e2b364a0":{"479f4b09-7fe2-4513-912c-6aca8bb4ffdd":"30dd6cde-be9b-4410-a136-fa203ba6fb0d"},65ee3274-d0c5-4780-8860-9f9fc1005d87:{dc720702-1667-4168-821a-1bf1612bb0cc:"3dc4f09b-97b1-4cb3-94c9-11ac71ee9899"},66efab1e-28ad-4d93-b715-490720be13fe:{b563ebee-a1ec-4a6d-a24c-0ba9bd387ad7:"e5a94781-0f1b-441e-86f7-a3a8e7506bee"},67c99d24-5a43-46c4-a34c-05b9ff21283c:"Test2"},captchas:{},isCompleted:true, password:""})
    //   });
    // fetch("https://forms.shakebugs.com/sheldon_feedback", {
    //   method: "POST",
    //   body: JSON.stringify({ tally: "+" })
    // }).then(() => {
    //   displayToast({ title: "Thank you for your feedback.", content: "This will help us in improving our AI agent." });
    // }).catch(() => {
    //   displayToast({ title: "Something went wrong", content: "Please try again." });
    // });
    displayToast({ title: "Thank you for your feedback.", content: "This will help us in improving our AI agent." });
  };

  const renderDuplicatesSuggestion = () => {
    return showDuplicate && duplicateSuggestion && onRemoveDuplicate && onMergeDuplicate ? (
      <div>
        {`By the way, I noticed a similar ticket that might be duplicate of this one. Want me to mark this ticket as a duplicate of `}
        <Link
          to={generatePath(RoutePaths.USER_FEEDBACK_DETAILS, {
            workspaceSlug: selectedWorkspace?.slug ?? null,
            appKey: selectedApp?.key ?? null,
            userFeedbackKey: duplicateSuggestion.key.toString(),
          })}
        >
          {duplicateSuggestion.pretty_title}
        </Link>
        {"? "}
        <Styled.Button onClick={() => onMergeDuplicate()}>{"Mark"}</Styled.Button>
      </div>
    ) : null;
  };

  const renderAiAssistants = () => {
    return (
      <div>
        Or, ask your own tool with one click:&nbsp;&nbsp;&nbsp;
        {aiAssistants.map((a) => (
          <AiAssistantButton
            key={a.name}
            aiAssistant={a}
            tooltip={isPreparingAiPrompt ? "Loading, please wait..." : `Click to ask your ${a.name} about this ticket`}
            onClick={() => onAiAssistantClick(a)}
            disabled={isPreparingAiPrompt}
          />
        ))}
      </div>
    );
  };

  const renderMcpPromo = () => {
    return (
      <div>
        You can also connect to Shake’s MCP server:&nbsp;&nbsp;&nbsp;
        <Styled.Button onClick={() => window.open(docsLinks.generic.mcp, "_blank")}>{"Learn more"}</Styled.Button>
      </div>
    );
  };

  return (
    <Styled.SheldonSummaryWrapper>
      <Styled.SheldonImage
        src={Sheldon}
        alt="AI Response"
      />
      <Flex style={{ width: "100%", marginTop: "2rem" }}>
        <img
          src={ArrowLeft}
          style={{ position: "relative", left: "1.2rem" }}
          width={22}
          height={18}
        />
        <Styled.ResponseWrapper>
          <Styled.HeaderWrapper>
            <Styled.Title p1>Sheldon</Styled.Title>
            <img
              src={Sparkle}
              alt="AI Sparkle"
            />
            <Styled.Feedback>
              <Tooltip
                style={{ height: "1.8rem" }}
                text={"Good summary"}
                position={"top"}
              >
                <img
                  src={ThumbsUp}
                  alt="AI Thumbs Up"
                  onClick={submitTally}
                />
              </Tooltip>
              <Tooltip
                style={{ height: "1.8rem" }}
                text={"Needs improvement"}
                position={"top"}
              >
                <img
                  src={ThumbsUp}
                  alt="AI Thumbs Down"
                  style={{ transform: "scaleY(-1)" }}
                  onClick={() => {
                    cancelModal.setOpen(true);
                  }}
                />
              </Tooltip>
              <Tooltip
                style={{ height: "1.8rem" }}
                text={"Privacy framework"}
                position={"top"}
              >
                <img
                  src={Privacy}
                  alt="AI Privacy"
                  onClick={() => showIntercomArticle(helpCenterArticles.aiPrivacy)}
                />
              </Tooltip>
            </Styled.Feedback>
          </Styled.HeaderWrapper>
          <Styled.ResponseBody
            p2
            color={vars.colors.grey50}
          >
            {llmResponse ? (
              <Linkify options={{ target: "_blank" }}>
                <div>{llmResponse.summary}</div>
                {llmResponse.type && llmResponse.priority && (
                  <div>
                    Seems like a {llmResponse.type} to me{" "}
                    <Styled.Button
                      // TODO: Add check if tag exists
                      onClick={() => onAddTag({ name: llmResponse?.type, active: true })}
                    >
                      Add tag
                    </Styled.Button>{" "}
                    and I&#39;d set its priority to {llmResponse.priority}{" "}
                    <Styled.Button
                      // TODO: Add check if priority already set
                      onClick={changePriority}
                    >
                      Set
                    </Styled.Button>{" "}
                    .
                  </div>
                )}
                {llmResponse.cause && <div>{llmResponse.cause}</div>}
                {llmResponse.log_ids && llmResponse.log_ids.length > 0 && (
                  <div>
                    I&#39;d like for you to take a look at these {llmResponse.log_ids.length} logs.{" "}
                    <Styled.Button
                      onClick={() =>
                        navigate(
                          generatePath(RoutePaths.USER_FEEDBACK_LOGS, {
                            workspaceSlug: params.workspaceSlug ?? null,
                            appKey: params.appKey ?? null,
                            userFeedbackKey: params.userFeedbackKey ?? null,
                          }).concat(`?search=${window.btoa(encodeURIComponent(JSON.stringify(searchParams)))}`),
                        )
                      }
                    >
                      See
                    </Styled.Button>
                  </div>
                )}
                {renderDuplicatesSuggestion()}
                {renderAiAssistants()}
                {renderMcpPromo()}
              </Linkify>
            ) : (
              <>
                <div>
                  Hi there! I&#39;m your AI colleague, a triaging agent. I can give you the gist, flag unusual logs, and
                  surface what matters. Want a quick overview?{" "}
                  <Styled.Button onClick={() => onGenerateResponse()}>Yes</Styled.Button>
                </div>
                {renderDuplicatesSuggestion()}
                {renderAiAssistants()}
                {renderMcpPromo()}
              </>
            )}
          </Styled.ResponseBody>
        </Styled.ResponseWrapper>
      </Flex>
      <BaseDialog {...cancelModal}>
        <AIFeedbackModal
          userFeedbackId={userFeedbackId}
          onCancel={() => {
            cancelModal.setOpen(false);
          }}
        />
      </BaseDialog>
    </Styled.SheldonSummaryWrapper>
  );
}
